'use client';

import {
  Captions,
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  useMediaRemote,
} from '@vidstack/react';
import { usePlayer } from 'contexts/PlayerContext';
import { useAppSelector } from 'lib/hooks';
import { RootState } from 'lib/store';
import { selectDisplayClosedCaptions } from 'slices/selectors';
import { useCaptionVisibility } from 'hooks/useCaptionVisibility';
import { useTextTrackMode } from 'hooks/useTextTrackMode';
import { useTextTracks } from 'hooks/useTextTracks';
import { ReactElement, useCallback, useEffect, useRef, useMemo } from 'react';
import { shouldAdvanceAfterAudioFailure } from 'utils/responsePlayback';

interface AudioComponentProps {
  //  direct source URL (for backward compatibility)
  source: string;
  // Optional callback when audio ends
  onAudioEnd?: () => void;
  // Optional closed captions track URL
  track?: string;
  // Audio type for different behaviors
  type?: 'bridge' | 'response' | 'step';
  // Whether to auto-play (default: true)
  autoPlay?: boolean;
  // Whether to show controls (default: false)
  showControls?: boolean;
  // Custom className
  className?: string;
  // For switching audio - when this changes, current audio stops and new one plays
  currentAudioSource?: string | null;
  // Whether to force stop current audio before playing new one
  forceStop?: boolean;
  // Optional callback when audio continues
  onContinue: () => void;
}

const AudioComponent: React.FC<AudioComponentProps> = ({
  source,
  track,
  type,
  showControls = false,
  className = '',
  onContinue,
  onAudioEnd,
}): ReactElement => {
  const playerRef = useRef<MediaPlayerInstance>(null);
  const audioWrapperRef = useRef<HTMLDivElement>(null);

  const { setPlayer } = usePlayer();
  const { volume, isMuted } = useAppSelector((state: RootState) => state.media);

  const showClosedCaptions = useAppSelector((state: RootState) =>
    selectDisplayClosedCaptions(state)
  );

  const remote = useMediaRemote(playerRef);

  // Set player reference in context when component mounts
  useEffect(() => {
    setPlayer(playerRef);
  }, [setPlayer, source, playerRef]);

  // Track caption visibility
  const captionsVisible = useCaptionVisibility(
    audioWrapperRef,
    source,
    showClosedCaptions
  );

  // Manage text track mode
  useTextTrackMode(playerRef, remote, showClosedCaptions, source, track);

  // Manage text tracks
  const textTracks = useMemo(
    () =>
      track
        ? [
            {
              src: track,
              kind: 'captions' as const,
              label: 'Captions',
              language: 'en',
              type: 'vtt' as const,
            },
          ]
        : [],
    [track]
  );

  useTextTracks(playerRef, remote, showClosedCaptions, source, textTracks);

  const advancedRef = useRef(false);
  const advanceAfterResponse = useCallback(() => {
    // iOS will not start unmuted audio from the post-tap autoplay effect.
    // Response clips are the gate to the next screen, so a blocked play
    // used to leave the member on the question no matter where they tapped.
    if (advancedRef.current || !shouldAdvanceAfterAudioFailure(type)) return;
    advancedRef.current = true;
    onContinue();
  }, [onContinue, type]);

  return (
    <div
      ref={audioWrapperRef}
      style={{ position: 'fixed', bottom: 0 }}
      className={`audio-component ${className}`}
    >
      <MediaPlayer
        autoPlay
        key={source}
        onError={() => advanceAfterResponse()}
        onPlayFail={advanceAfterResponse}
        onAutoPlayFail={advanceAfterResponse}
        playsInline
        preload="metadata"
        ref={playerRef}
        src={source}
        onEnded={() => {
          onAudioEnd?.();
          advanceAfterResponse();
        }}
        volume={volume}
        muted={isMuted}
        controls={showControls}
      >
        <MediaProvider />

        {showClosedCaptions && (
          <Captions
            className="vds-captions"
            style={{
              display: captionsVisible ? 'block' : 'none',
            }}
          />
        )}
      </MediaPlayer>
    </div>
  );
};

export default AudioComponent;
